import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import AppShell, { PageContainer, PageHeader } from "@/components/AppShell";
import { useAuth } from "@/lib/AuthContext";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Globe2, Languages, Gamepad2, Trophy, Sparkles, Users, X, Search } from "lucide-react";

const SKILL_LABELS = {
  casual: "Casual",
  intermediate: "Intermediate",
  competitive: "Competitive",
  pro: "Pro",
};

const EXP_LABELS = {
  rookie: "Rookie",
  veteran: "Veteran",
  hardcore: "Hardcore",
};

const SKILL_COLORS = {
  casual: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  intermediate: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  competitive: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  pro: "bg-rose-500/10 text-rose-300 border-rose-500/30",
};

export default function PlayersPage() {
  const { t } = useT();
  const { profile } = useAuth();
  const [params, setParams] = useSearchParams();

  const initialGame = params.get("game") || "";
  const [game, setGame] = useState(initialGame);
  const [country, setCountry] = useState(params.get("country") || "");
  const [language, setLanguage] = useState(params.get("language") || "");
  const [skill, setSkill] = useState(params.get("skill_level") || "");

  const [games, setGames] = useState([]);
  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);

  // Load metadata for filter dropdowns
  useEffect(() => {
    Promise.all([
      api.get("/games").catch(() => ({ data: { items: [] } })),
      api.get("/meta/countries").catch(() => ({ data: { items: [] } })),
      api.get("/meta/languages").catch(() => ({ data: { items: [] } })),
    ]).then(([g, c, l]) => {
      setGames(g.data.items || []);
      setCountries(c.data.items || []);
      setLanguages(l.data.items || []);
    });
  }, []);

  // Re-fetch on any filter change
  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (game) q.set("game", game);
    if (country) q.set("country", country);
    if (language) q.set("language", language);
    if (skill) q.set("skill_level", skill);
    api
      .get(`/players?${q.toString()}`)
      .then((r) => {
        setItems(r.data.items || []);
        setCount(r.data.count || 0);
      })
      .catch(() => {
        setItems([]);
        setCount(0);
      })
      .finally(() => setLoading(false));

    // Sync URL so the link is shareable
    const next = {};
    if (game) next.game = game;
    if (country) next.country = country;
    if (language) next.language = language;
    if (skill) next.skill_level = skill;
    setParams(next, { replace: true });
  }, [game, country, language, skill, setParams]);

  const myFavGames = profile?.games || [];

  const clearAll = () => {
    setGame("");
    setCountry("");
    setLanguage("");
    setSkill("");
  };

  const activeFilterCount = useMemo(
    () => [game, country, language, skill].filter(Boolean).length,
    [game, country, language, skill]
  );

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          kicker={t("players.kicker") || "MATCHMAKING"}
          title={t("players.title") || "Mitspieler finden"}
          subtitle={
            t("players.sub") ||
            "Finde Pilots mit gleichem Spiel, Land, Sprache und Skill — sortiert nach Match-Score."
          }
        />

        {/* Quick game chips from favorites */}
        {myFavGames.length > 0 && (
          <div className="mb-6">
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono mb-2">
              {t("players.your_favs") || "Deine Lieblingsgames"}
            </p>
            <div className="flex flex-wrap gap-2" data-testid="players-fav-chips">
              {myFavGames.map((g) => (
                <button
                  key={g}
                  onClick={() => setGame(g)}
                  data-testid={`players-fav-${g}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    game === g
                      ? "bg-neon text-black border-neon shadow-neon"
                      : "bg-white/5 border-white/10 text-zinc-300 hover:border-neon/40 hover:text-neon"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div
          className="rounded-2xl border border-white/5 bg-zinc-900/50 p-4 sm:p-5 mb-6 grid grid-cols-2 sm:grid-cols-4 gap-3"
          data-testid="players-filters"
        >
          <FilterSelect
            id="players-filter-game"
            icon={Gamepad2}
            label={t("players.f_game") || "Spiel"}
            value={game}
            onChange={setGame}
            options={games.map((g) => ({ value: g.name, label: g.name }))}
          />
          <FilterSelect
            id="players-filter-country"
            icon={Globe2}
            label={t("players.f_country") || "Land"}
            value={country}
            onChange={setCountry}
            options={countries.map((c) => ({ value: c.code, label: `${c.flag} ${c.name}` }))}
          />
          <FilterSelect
            id="players-filter-language"
            icon={Languages}
            label={t("players.f_lang") || "Sprache"}
            value={language}
            onChange={setLanguage}
            options={languages.map((l) => ({ value: l.code, label: l.name }))}
          />
          <FilterSelect
            id="players-filter-skill"
            icon={Trophy}
            label={t("players.f_skill") || "Skill"}
            value={skill}
            onChange={setSkill}
            options={Object.entries(SKILL_LABELS).map(([v, label]) => ({ value: v, label }))}
          />
          {activeFilterCount > 0 && (
            <button
              onClick={clearAll}
              data-testid="players-clear-filters"
              className="col-span-2 sm:col-span-4 inline-flex items-center justify-center gap-1.5 text-xs text-zinc-400 hover:text-white"
            >
              <X size={14} />
              {t("players.clear") || "Filter zurücksetzen"} ({activeFilterCount})
            </button>
          )}
        </div>

        {/* Result strip */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-zinc-400" data-testid="players-result-count">
            {loading
              ? t("players.searching") || "Suche läuft..."
              : `${count} ${count === 1 ? (t("players.found_one") || "Pilot gefunden") : (t("players.found_many") || "Pilots gefunden")}`}
          </p>
          {!loading && count > 0 && (
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
              {t("players.sorted_by") || "Sortiert nach Match-Score"}
            </span>
          )}
        </div>

        {/* States */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="players-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState t={t} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="players-grid">
            {items.map((p, i) => (
              <PlayerCard key={p.user_id} player={p} index={i} t={t} />
            ))}
          </div>
        )}
      </PageContainer>
    </AppShell>
  );
}

function FilterSelect({ id, icon: Icon, label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">
        <Icon size={11} className="text-neon" />
        {label}
      </span>
      <select
        data-testid={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 rounded-xl bg-zinc-950 border border-white/10 px-2.5 text-sm text-white focus:border-neon focus:outline-none focus:ring-1 focus:ring-neon"
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function PlayerCard({ player, index, t }) {
  const skillKey = (player.skill_level || "").toLowerCase();
  const expKey = (player.experience_level || "").toLowerCase();
  return (
    <article
      className="relative overflow-hidden rounded-2xl border border-white/5 bg-zinc-900/60 p-5 animate-fade-up flex flex-col"
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`player-card-${player.user_id}`}
    >
      <div
        className="absolute -top-1 -right-1 px-2.5 py-1 rounded-bl-xl rounded-tr-2xl bg-neon text-black font-display font-black text-xs shadow-neon"
        data-testid={`player-match-score-${player.user_id}`}
      >
        {player.match_score} pts
      </div>

      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-neon to-blue-500 grid place-items-center text-black font-display font-black text-xl">
          {(player.nickname || "P").slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-lg font-bold truncate" data-testid="player-nickname">
            @{player.nickname}
          </h3>
          <div className="text-[11px] text-zinc-500 truncate">
            {player.country || "—"}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {skillKey && SKILL_LABELS[skillKey] && (
          <span
            className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${SKILL_COLORS[skillKey]}`}
          >
            {SKILL_LABELS[skillKey]}
          </span>
        )}
        {expKey && EXP_LABELS[expKey] && (
          <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border bg-white/5 border-white/10 text-zinc-300">
            {EXP_LABELS[expKey]}
          </span>
        )}
      </div>

      {(player.languages || []).length > 0 && (
        <p className="mt-3 inline-flex flex-wrap items-center gap-1.5 text-[11px] text-zinc-400">
          <Languages size={12} className="text-zinc-500" />
          {player.languages.join(" · ")}
        </p>
      )}

      {(player.games || []).length > 0 && (
        <p className="mt-1.5 inline-flex flex-wrap items-center gap-1.5 text-[11px] text-zinc-400">
          <Gamepad2 size={12} className="text-zinc-500" />
          {player.games.slice(0, 4).join(" · ")}
          {player.games.length > 4 && (
            <span className="text-zinc-600"> +{player.games.length - 4}</span>
          )}
        </p>
      )}

      <div className="mt-5 flex gap-2 pt-4 border-t border-white/5">
        <Button
          size="sm"
          className="flex-1 h-9 rounded-full bg-neon text-black hover:bg-neon-hover font-bold"
          data-testid={`player-invite-${player.user_id}`}
          asChild
        >
          <Link to="/app/teammates">
            <Users size={14} className="mr-1.5" />
            {t("players.invite") || "Einladen"}
          </Link>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-9 rounded-full bg-transparent border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white"
          data-testid={`player-coach-${player.user_id}`}
          asChild
        >
          <Link to="/app/coach">
            <Sparkles size={14} className="mr-1.5" />
            {t("players.coach") || "Coach"}
          </Link>
        </Button>
      </div>
    </article>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-white/5 bg-zinc-900/40 p-5 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-white/5" />
        <div className="flex-1">
          <div className="h-3 bg-white/10 rounded w-24" />
          <div className="mt-2 h-2 bg-white/5 rounded w-16" />
        </div>
      </div>
      <div className="mt-4 h-2 bg-white/5 rounded w-2/3" />
      <div className="mt-2 h-2 bg-white/5 rounded w-1/2" />
      <div className="mt-5 h-9 bg-white/5 rounded-full" />
    </div>
  );
}

function EmptyState({ t }) {
  return (
    <div
      className="rounded-3xl border border-dashed border-white/10 bg-zinc-900/30 p-10 text-center"
      data-testid="players-empty"
    >
      <div className="w-14 h-14 rounded-2xl bg-neon/10 border border-neon/25 grid place-items-center text-neon mx-auto">
        <Search size={24} />
      </div>
      <h3 className="mt-4 font-display text-xl font-bold">
        {t("players.empty_h") || "Noch keine Pilots gefunden"}
      </h3>
      <p className="mt-2 text-sm text-zinc-400 max-w-sm mx-auto">
        {t("players.empty_sub") ||
          "Lockere die Filter oder lade Freunde ein — sobald mehr Pilots ihr Profil vervollständigen, erscheinen sie hier sortiert nach Match-Score."}
      </p>
      <div className="mt-5 flex justify-center gap-3">
        <Button asChild size="sm" className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold">
          <Link to="/app/profile">{t("players.empty_cta_profile") || "Profil ergänzen"}</Link>
        </Button>
        <Button
          asChild
          size="sm"
          variant="outline"
          className="rounded-full bg-transparent border-white/10 text-white hover:bg-white/5"
        >
          <Link to="/app/teammates">{t("players.empty_cta_team") || "Teammates ansehen"}</Link>
        </Button>
      </div>
    </div>
  );
}
